import { useEffect, useRef, useState } from "react";
import styles from "./EstimateLayoutStep5.module.scss";
import classNames from "classnames/bind";
import FormInput from "~/components/common/form/formInput/FormInput";
import BaseSelectCustom from "~/components/common/base-select-custom/BaseSelectCustom";
import { DragIcon } from "~/assets/icons";
import AddItem from "~/components/common/add-item/AddItem";
import DroppableArea from "~/components/common/drop-drag/DroppableArea";
import BaseBoxContent from "~/components/common/base-box-content/BaseBoxContent";
import FormToggle from "~/components/common/form/formToggle/FormToggle";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { EstimateUoMOption } from "~/pages/admin/jobs/add-job/constants/add-estimate.constants";
import type {
    EstimateLayoutStep5FormData as Step5FormData,
    IAuthorizationSectionItem,
    IAuthorizationOptional,
    IProductSelectionItem,
} from "~/utils/interface/estimateSetting";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetEstimateTemplateAuthorization } from "~/thunks/estimate-setting/estimateSettingThunk";
import type { IEstimateTemplateAuthorization } from "~/utils/interface/estimateSetting";

const cx = classNames.bind(styles);

const DEFAULT_SECTION_ITEM: IAuthorizationSectionItem = {
    itemName: "",
    type: "MATERIAL",
    uom: "",
    quantity: 0,
    unitPrice: 0,
};

const DEFAULT_OPTIONAL: IAuthorizationOptional = {
    title: "Sections",
    isVisible: true,
    sections: [{ ...DEFAULT_SECTION_ITEM }],
};

const DEFAULT_SELECTION_ITEM: IProductSelectionItem = {
    name: "",
    customer: "",
};

interface EstimateLayoutStep5Props {
    onStep5DataChange?: (data: Step5FormData) => void;
    initialData?: Step5FormData | null;
}

const EstimateLayoutStep5 = ({ onStep5DataChange, initialData }: EstimateLayoutStep5Props) => {
    const dispatch = useAppDispatch();
    const { estimateTemplateAuthorization } = useAppSelector((state) => state.estimateSetting);

    const [formData, setFormData] = useState<Step5FormData>(() => {
        const data = initialData ?? undefined;
        if (!data?.optional?.sections?.length) {
            return {
                titleStep5: data?.titleStep5 ?? "Authorization Page",
                disclaimer: data?.disclaimer ?? "",
                signer: data?.signer ?? { firstName: "", lastName: "", email: "" },
                isDeposit: data?.isDeposit ?? false,
                footerNotes: data?.footerNotes ?? "",
                optional: { ...DEFAULT_OPTIONAL, ...data?.optional, sections: data?.optional?.sections?.length ? data.optional.sections : DEFAULT_OPTIONAL.sections },
                selection: data?.selection ?? {
                    title: "My Product Selections",
                    selections: [
                        { ...DEFAULT_SELECTION_ITEM },
                        { ...DEFAULT_SELECTION_ITEM },
                        { ...DEFAULT_SELECTION_ITEM },
                    ],
                },
            };
        }
        const sel = data?.selection;
        const selections = sel?.selections?.length
            ? sel.selections
            : [{ ...DEFAULT_SELECTION_ITEM }, { ...DEFAULT_SELECTION_ITEM }, { ...DEFAULT_SELECTION_ITEM }];
        return {
            titleStep5: data?.titleStep5 ?? "Authorization Page",
            disclaimer: data?.disclaimer ?? "",
            signer: data?.signer ?? { firstName: "", lastName: "", email: "" },
            isDeposit: data?.isDeposit ?? false,
            footerNotes: data?.footerNotes ?? "",
            optional: data?.optional ?? DEFAULT_OPTIONAL,
            selection: { ...sel, title: sel?.title ?? "My Product Selections", selections },
        };
    });

    const notifyParent = (data: Step5FormData) => {
        setFormData(data);
        onStep5DataChange?.(data);
    };

    const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const templateDropdownRef = useRef<HTMLDivElement>(null);
    const formDataRef = useRef(formData);

    const handleUpdateFormData = (update: Partial<Step5FormData>) => {
        const data: Step5FormData = { ...formData, ...update };
        notifyParent(data);
    };

    const handleUpdateSigner = (update: Partial<Step5FormData["signer"]>) => {
        const signer = { ...(formData.signer ?? {}), ...update };
        notifyParent({ ...formData, signer });
    };

    const handleSectionUpdate = (update: Partial<IAuthorizationSectionItem>, indexSection: number) => {
        const optional = { ...formData.optional } as IAuthorizationOptional;
        const sections = [...(optional.sections ?? [])];
        sections[indexSection] = { ...sections[indexSection], ...update };
        optional.sections = sections;
        notifyParent({ ...formData, optional });
    };

    const handleAddSection = () => {
        const optional = { ...formData.optional } as IAuthorizationOptional;
        const sections = [...(optional.sections ?? []), { ...DEFAULT_SECTION_ITEM }];
        optional.sections = sections;
        notifyParent({ ...formData, optional });
    };

    const handleRemoveSection = (indexSection: number) => {
        const optional = { ...formData.optional } as IAuthorizationOptional;
        const sections = [...(optional.sections ?? [])];
        sections.splice(indexSection, 1);
        optional.sections = sections.length ? sections : [{ ...DEFAULT_SECTION_ITEM }];
        notifyParent({ ...formData, optional });
    };

    const handleDropSections = (newSections: IAuthorizationSectionItem[]) => {
        const optional = { ...formData.optional, sections: newSections } as IAuthorizationOptional;
        notifyParent({ ...formData, optional });
    };

    const handleUpdateSelectionItem = (update: Partial<IProductSelectionItem>, index: number) => {
        const selection = { ...formData.selection };
        const selections = [...(selection?.selections ?? [])];
        selections[index] = { ...selections[index], ...update };
        selection.selections = selections;
        notifyParent({ ...formData, selection });
    };

    const handleDropSelections = (newSelections: IProductSelectionItem[]) => {
        const selection = { ...formData.selection, selections: newSelections };
        notifyParent({ ...formData, selection });
    };

    const totalSection = (sections: IAuthorizationSectionItem[] = []) => {
        return sections
            .reduce((sum, s) => sum + (Number(s.quantity) || 0) * (Number(s.unitPrice) || 0), 0)
            .toFixed(2);
    };

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target as Node)) {
                setTemplateDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const data = initialData ?? undefined;
        if (data?.optional?.sections?.length || data?.selection?.selections?.length) {
            setFormData({
                titleStep5: data.titleStep5 ?? "Authorization Page",
                disclaimer: data.disclaimer ?? "",
                signer: data.signer ?? { firstName: "", lastName: "", email: "" },
                isDeposit: data.isDeposit ?? false,
                footerNotes: data.footerNotes ?? "",
                optional: data.optional ?? DEFAULT_OPTIONAL,
                selection: data.selection ?? {
                    title: "My Product Selections",
                    selections: [
                        { ...DEFAULT_SELECTION_ITEM },
                        { ...DEFAULT_SELECTION_ITEM },
                        { ...DEFAULT_SELECTION_ITEM },
                    ],
                },
            });
        }
    }, [initialData]);

    const sections = formData.optional?.sections ?? [];
    const selections = formData.selection?.selections ?? [];

    const handleUseTemplateClick = () => {
        const nextOpen = !templateDropdownOpen;
        setTemplateDropdownOpen(nextOpen);
        if (nextOpen) {
            setTemplatesLoading(true);
            dispatch(handleGetEstimateTemplateAuthorization()).finally(() => {
                setTemplatesLoading(false);
            });
        }
    };

    const handleSelectTemplate = (template: IEstimateTemplateAuthorization) => {
        const itemsSorted = [...(template.items ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        const selectionsSorted = [...(template.selections ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

        const selectionItems: IProductSelectionItem[] = selectionsSorted
            .slice(0, 3)
            .map((s) => ({
                name: s.fieldName ?? "",
                customer: s.customerInput ?? "",
            }));
        while (selectionItems.length < 3) {
            selectionItems.push({ ...DEFAULT_SELECTION_ITEM });
        }

        const next: Step5FormData = {
            ...formDataRef.current,
            titleStep5: template.estimateTemplate?.templateName ?? formDataRef.current.titleStep5 ?? "Authorization Page",
            disclaimer: template.disclaimer ?? formDataRef.current.disclaimer ?? "",
            isDeposit: template.isDeposit ?? formDataRef.current.isDeposit ?? false,
            footerNotes: template.footer ?? formDataRef.current.footerNotes ?? "",
            optional: {
                ...(formDataRef.current.optional ?? DEFAULT_OPTIONAL),
                title: template.optionalTitle ?? formDataRef.current.optional?.title ?? "Sections",
                isVisible: formDataRef.current.optional?.isVisible ?? true,
                sections: itemsSorted.length
                    ? itemsSorted.map((item) => ({
                        itemName: item.itemName ?? "",
                        type: item.type ?? "MATERIAL",
                        uom: item.uom ?? "",
                        quantity: Number(item.quantity ?? 0) || 0,
                        unitPrice: Number(item.unitPrice ?? 0) || 0,
                    }))
                    : [{ ...DEFAULT_SECTION_ITEM }],
            },
            selection: {
                ...(formDataRef.current.selection ?? { title: "My Product Selections", selections: [] }),
                title: template.selectionsTitle ?? formDataRef.current.selection?.title ?? "My Product Selections",
                selections: selectionItems,
            },
        };

        formDataRef.current = next;
        notifyParent(next);
        setTemplateDropdownOpen(false);
    };

    return (
        <div className={cx("estimate-layout-step-5-container")}>
            <div className={cx("header")}>
                <TitleEditor
                    value={formData.titleStep5 ?? ""}
                    name=""
                    placeholder=""
                    inputkey=""
                    isViewMode={false}
                    onChange={(value) => handleUpdateFormData({ titleStep5: value })}
                    styleView={{ fontSize: "20px", fontWeight: 700 }}
                />
                <div className={cx("button-container")}>
                    <div className={cx("template-dropdown-area")} ref={templateDropdownRef}>
                        <div className={cx("template-dropdown-wrapper")}>
                            <OutlinedButton
                                title="Use Template"
                                onClick={handleUseTemplateClick}
                                className={cx("use-template")}
                            />
                            {templateDropdownOpen && (
                                <div className={cx("template-dropdown")}>
                                    {templatesLoading ? (
                                        <div className={cx("template-dropdown-loading")}>Loading...</div>
                                    ) : (
                                        <ul className={cx("template-dropdown-list")}>
                                            {estimateTemplateAuthorization.length === 0 ? (
                                                <li className={cx("template-dropdown-empty")}>No templates</li>
                                            ) : (
                                                estimateTemplateAuthorization.map((template) => (
                                                    <li key={template.id}>
                                                        <button
                                                            type="button"
                                                            className={cx("template-dropdown-item")}
                                                            onClick={() => handleSelectTemplate(template)}
                                                        >
                                                            {template.estimateTemplate?.templateName || "Unnamed template"}
                                                        </button>
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <OutlinedButton
                        title="Page Settings"
                        onClick={() => { }}
                        className={cx("page-setting")}
                    />

                    <PrimaryButton title="View Page" onClick={() => { }} className={cx("btn-preview")} />
                </div>
            </div>

            <div className={cx("content")}>
                {/* Section Disclaimer */}
                <div className={cx("disclaimer-block")}>
                    <p className={cx("section-label")}>Section Disclaimer</p>
                    <FormInput
                        name="Disclaimer"
                        text="Disclaimer"
                        isViewMode={false}
                        placeholder="Disclaimer"
                        inputkey="disclaimer"
                        value={formData.disclaimer ?? ""}
                        height="56px"
                        onChange={(e) => handleUpdateFormData({ disclaimer: e.target.value })}
                    />
                </div>

                {/* Sections: Item, UoM, Quantity, Price, Line Total */}
                <BaseBoxContent className={cx("section-item")} expanded>
                    <div data-slot="header">
                        <div className={cx("section-title")}>
                            <div className={cx("drag-button")}>
                                <DragIcon className={cx("drag-icon")} />
                            </div>
                            <TitleEditor
                                value={formData.optional?.title ?? "Sections"}
                                name=""
                                placeholder=""
                                inputkey=""
                                isViewMode={false}
                                onChange={(value) =>
                                    handleUpdateFormData({
                                        optional: { ...formData.optional, title: value },
                                    })
                                }
                                styleView={{ fontSize: "16px", fontWeight: 500 }}
                            />
                        </div>
                    </div>
                    <div data-slot="content">
                        <div className={cx("section-item-table")}>
                            <div className={cx("section-item-header")}>
                                <div className="w-[24px]" />
                                <div className="w-[28%]">
                                    <h3>Item</h3>
                                </div>
                                <div className="w-[16%]">
                                    <h3>UoM</h3>
                                </div>
                                <div className="w-[16%]">
                                    <h3>Quantity</h3>
                                </div>
                                <div className="w-[16%]">
                                    <h3>Price</h3>
                                </div>
                                <div className="w-[19%]">
                                    <h3>Line Total</h3>
                                </div>
                                <div className="w-[5%]" />
                            </div>
                            <div className={cx("section-item-table-body")}>
                                <DroppableArea
                                    data={sections}
                                    groupName="step5-sections"
                                    onChange={handleDropSections}
                                    renderComponent={(section: IAuthorizationSectionItem, indexSection: number) => (
                                        <div className={cx("section-item-table-row")}>
                                            <div className={cx("drag-button")}>
                                                <DragIcon className={cx("drag-icon")} />
                                            </div>
                                            <div className="w-[28%]">
                                                <FormInput
                                                    name="itemName"
                                                    text=""
                                                    isViewMode={false}
                                                    isViewNoInput
                                                    placeholder="Item"
                                                    inputkey="itemName"
                                                    value={section.itemName ?? ""}
                                                    onChange={(e) =>
                                                        handleSectionUpdate({ itemName: e.target.value }, indexSection)
                                                    }
                                                    height="31px"
                                                />
                                            </div>
                                            <div className="w-[16%]">
                                                <BaseSelectCustom
                                                    selectedView={section.uom ?? ""}
                                                    onViewChange={(viewId) =>
                                                        handleSectionUpdate(
                                                            { uom: viewId != null ? String(viewId) : "" },
                                                            indexSection
                                                        )
                                                    }
                                                    height="36px"
                                                    isViewMode={false}
                                                    isViewNoInput
                                                    viewOptions={EstimateUoMOption}
                                                    placeholder="UoM"
                                                    className={cx("select-filter")}
                                                    minWidth="100px"
                                                    textAlign="left"
                                                    IconDrop={
                                                        <div className={cx("drop")}>
                                                            <svg
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M11.9994 14.308L8.19141 10.5H15.8074L11.9994 14.308Z"
                                                                    fill="black"
                                                                />
                                                            </svg>
                                                        </div>
                                                    }
                                                />
                                            </div>
                                            <div className="w-[16%]">
                                                <FormInput
                                                    name="quantity"
                                                    text=""
                                                    value={section.quantity ?? ""}
                                                    height="31px"
                                                    isViewMode={false}
                                                    isViewNoInput
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleSectionUpdate(
                                                            { quantity: Number(e.target.value) || 0 },
                                                            indexSection
                                                        )
                                                    }
                                                    inputkey=""
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="w-[16%]">
                                                <FormInput
                                                    name="unitPrice"
                                                    text=""
                                                    isViewMode={false}
                                                    isViewNoInput
                                                    placeholder="0"
                                                    inputkey="unitPrice"
                                                    value={section.unitPrice ?? ""}
                                                    onChange={(e) =>
                                                        handleSectionUpdate(
                                                            { unitPrice: Number(e.target.value) || 0 },
                                                            indexSection
                                                        )
                                                    }
                                                    height="31px"
                                                />
                                            </div>
                                            <div className="w-[19%]">
                                                <div className={cx("total-money")}>
                                                    <h3>
                                                        $
                                                        {(
                                                            (Number(section.quantity) || 0) *
                                                            (Number(section.unitPrice) || 0)
                                                        ).toFixed(2)}
                                                    </h3>
                                                </div>
                                            </div>
                                            <div className="w-[5%] flex items-center">
                                                <div
                                                    className={cx("text-delete")}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => handleRemoveSection(indexSection)}
                                                    onKeyDown={() => { }}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M3.33398 3.33398L12.6673 12.6673"
                                                            stroke="#FA6363"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                        <path
                                                            d="M3.33269 12.6673L12.666 3.33398"
                                                            stroke="#FA6363"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div data-slot="footer">
                        <div className={cx("section-action")}>
                            <AddItem onClick={handleAddSection} />
                        </div>
                        <div className={cx("section-item-footer")}>
                            <div className={cx("total")}>
                                <h4>Section Total:</h4>
                                <h3>${totalSection(sections)}</h3>
                            </div>
                        </div>
                    </div>
                </BaseBoxContent>

                {/* Deposit */}
                <div className={cx("deposit")}>
                    <div className={cx("deposit-row")}>
                        <p className={cx("deposit-title")}>Deposit</p>
                        <div>
                            <FormToggle text={""} inputkey={""} value={formData.isDeposit ?? false} onChange={(e) => {
                                handleUpdateFormData({ isDeposit: e.newValue })
                            }} />
                        </div>
                    </div>
                    <p className={cx("deposit-help")}>Description</p>
                </div>

                {/* Primary Signer */}
                <div className={cx("primary-signer")}>
                    <h4 className={cx("primary-signer-title")}>Primary Signer</h4>
                    <h4 className={cx("primary-signer-desc")}>Description</h4>
                    <div className={cx("primary-signer-fields")}>
                        <FormInput
                            name="firstName"
                            text="First Name"
                            isViewMode={false}
                            placeholder="John"
                            inputkey="firstName"
                            value={formData.signer?.firstName ?? ""}
                            height="56px"
                            onChange={(e) => handleUpdateSigner({ firstName: e.target.value })}
                        />
                        <FormInput
                            name="lastName"
                            text="Last Name"
                            isViewMode={false}
                            placeholder="Smith"
                            inputkey="lastName"
                            value={formData.signer?.lastName ?? ""}
                            height="56px"
                            onChange={(e) => handleUpdateSigner({ lastName: e.target.value })}
                        />
                        <FormInput
                            name="email"
                            text="Email Address"
                            isViewMode={false}
                            placeholder="john@company.com"
                            inputkey="email"
                            value={formData.signer?.email ?? ""}
                            height="56px"
                            onChange={(e) => handleUpdateSigner({ email: e.target.value })}
                        />
                    </div>
                </div>

                {/* My Product Selections */}
                <div className={cx("selection-block")}>
                    <div className={cx("primary-signer")}>
                        <TitleEditor
                            value={formData.selection?.title ?? "My Product Selections"}
                            name=""
                            placeholder=""
                            inputkey=""
                            isViewMode={false}
                            onChange={(value) =>
                                handleUpdateFormData({
                                    selection: { ...formData.selection, title: value },
                                })
                            }
                            styleView={{ fontSize: "16px", fontWeight: 600 }}
                        />
                        <h4 className={cx("primary-signer-desc")}>Description</h4>
                    </div>
                    <div className={cx("selections-list")}>
                        <DroppableArea
                            data={selections}
                            groupName="step5-selections"
                            onChange={handleDropSelections}
                            renderComponent={(item: IProductSelectionItem, index: number) => (
                                <div className={cx("production-selection")}>
                                    <div className={cx("selection-row")}>
                                        <div className={cx("drag-button")}>
                                            <DragIcon className={cx("drag-icon")} />
                                        </div>
                                        <span className={cx("selection-label")}>Selection {index + 1}</span>
                                    </div>
                                    <div className={cx("selection-fields")}>
                                        <div className={cx("selection-spacer")} />
                                        <div className={cx("selection-inputs")}>
                                            <FormInput
                                                name="item"
                                                text=""
                                                isViewMode={false}
                                                placeholder="Item"
                                                inputkey="item"
                                                value={item.name ?? ""}
                                                height="56px"
                                                onChange={(e) =>
                                                    handleUpdateSelectionItem({ name: e.target.value }, index)
                                                }
                                            />
                                            <FormInput
                                                name="customer"
                                                text=""
                                                isViewMode={false}
                                                placeholder="Customer Input"
                                                inputkey="customer"
                                                value={item.customer ?? ""}
                                                height="56px"
                                                onChange={(e) =>
                                                    handleUpdateSelectionItem({ customer: e.target.value }, index)
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                </div>

                {/* Footer Notes */}
                <div className={cx("footer-notes")}>
                    <FormInput
                        name="footerNotes"
                        text="Footer Notes"
                        placeholder=""
                        isViewMode={false}
                        inputkey="footerNotes"
                        value={formData.footerNotes ?? ""}
                        inputType="textarea"
                        height="121px"
                        onChange={(e) => handleUpdateFormData({ footerNotes: e.target.value })}
                    />
                </div>
            </div>
        </div>
    );
};

export default EstimateLayoutStep5;
